import { SengiColors } from "./SengiColors";

const DEFAULT_HOTSPOT = "2 20, crosshair";
const DEFAULT_TOOLKIT_SIZE = 32;
const CURSOR_BOTTOM_PADDING_RATIO = 0.1;
const CURSOR_SHIFT_UP_RATIO = 0;

const escapeSvgForCursor = (svg: string): string => {
  return svg
    .replace(/\n+/g, "")
    .replace(/\s{2,}/g, " ")
    .trim()
    .replace(/%/g, "%25")
    .replace(/#/g, "%23")
    .replace(/"/g, "'")
    .replace(/</g, "%3C")
    .replace(/>/g, "%3E")
    .replace(/\{/g, "%7B")
    .replace(/\}/g, "%7D")
    .replace(/\|/g, "%7C")
    .replace(/\^/g, "%5E")
    .replace(/`/g, "%60")
    .replace(/&/g, "%26");
}

const buildCursorString = (svg: string, hotspot = DEFAULT_HOTSPOT): string => {
  return `url("data:image/svg+xml;utf8,${escapeSvgForCursor(svg)}") ${hotspot}`;
};
const buildPaddedCursorSvg = (
  //TODO ADJUST PADDING OF PREVIEW
  content: string,
  size: number,
  viewBoxWidth: number,
  viewBoxHeight: number,
  bottomPaddingRatio: number = CURSOR_BOTTOM_PADDING_RATIO,
  shiftUpRatio: number = CURSOR_SHIFT_UP_RATIO,
): string => {
  const bottomPaddingUnits = viewBoxHeight * bottomPaddingRatio;
  const shiftUpUnits = viewBoxHeight * shiftUpRatio;

  // kleiner Sicherheitsraum oben für Strokes
  const topSafetyUnits = viewBoxHeight * 0.06;
  const topPaddingUnits = shiftUpUnits + topSafetyUnits;

  const totalViewBoxHeight = viewBoxHeight + topPaddingUnits + bottomPaddingUnits;

  // Wichtig: Höhe proportional mitwachsen lassen, damit die SVG nicht kleiner skaliert
  const renderedHeight = size * (totalViewBoxHeight / viewBoxHeight);

  return `
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="${size}"
      height="${renderedHeight}"
      viewBox="0 -${topPaddingUnits} ${viewBoxWidth} ${totalViewBoxHeight}"
    >
      <g transform="translate(0 -${shiftUpUnits})">
        ${content}
      </g>
    </svg>
  `;
};
const outlinedFilledPath = (
  d: string,
  fill: string,
  innerBlack = 2.4,
  outerWhite = 3.6,
): string => {
  return `
    <path d="${d}" fill="none" stroke="#ffffff" stroke-width="${outerWhite}" stroke-linejoin="round" stroke-linecap="round" />
    <path d="${d}" fill="none" stroke="#000000" stroke-width="${innerBlack}" stroke-linejoin="round" stroke-linecap="round" />
    <path d="${d}" fill="${fill}" />
  `;
};
const strokedPath = (
  d: string,
  innerBlack = 1.8,
  outerWhite = 3.0,
): string => {
  return `
    <path d="${d}" fill="none" stroke="#ffffff" stroke-width="${outerWhite}" stroke-linejoin="round" stroke-linecap="round" />
    <path d="${d}" fill="none" stroke="#000000" stroke-width="${innerBlack}" stroke-linejoin="round" stroke-linecap="round" />
  `;
};

const strokedLine = (d: string, strokeWidth = 1.0): string => {
  return `
    <path d="${d}" fill="none" stroke="#000000" stroke-width="${strokeWidth}" stroke-linecap="round" />
  `;
};

const brushSvg = (color: string, size:number= DEFAULT_TOOLKIT_SIZE,bottomPaddingRatio: number = CURSOR_BOTTOM_PADDING_RATIO, shiftUpRatio: number = CURSOR_SHIFT_UP_RATIO): string => {

    const ferrule = "M197.972,375.69c10.408-4.046,22.136-10.36,35.238-19.224c-4.207-14.417-11.977-28.004-23.325-39.352 c-14.761-14.762-33.638-23.892-54.038-26.378c-8.73,16.371-13.989,30.43-16.265,42.314 C164.477,338.471,185.536,354.277,197.972,375.69z";
    const tip = "M121,489.988c38.163-0.258,69.022-31.264,69.022-69.488c0-38.384-31.116-69.5-69.5-69.5 c-38.213,0-69.238,31.009-69.514,69.157c0-0.02,0-0.037,0-0.037s0,0.013,0,0.047C51.008,436,30.1,490,0,490h121V489.988z";
    const handle = "M487.834,1.77l-0.001,0.002l-0.195-0.178l0.002-0.002c-15.428-14.08-146.838,66.886-246.759,175.847 c-33.467,36.843-57.74,68.303-74.26,94.747c21.609,4.291,41.476,14.855,57.406,30.786c12.025,12.025,20.746,26.121,26.178,41.147 c24.256-18.757,52.518-45.183,84.994-80.596C434.604,154.091,503.262,15.851,487.834,1.77z";

    return buildPaddedCursorSvg(
      `
        ${outlinedFilledPath(handle, color, 24, 12)}
        ${outlinedFilledPath(ferrule, SengiColors.sng_white, 24, 12)}
        ${outlinedFilledPath(tip, color, 24, 12)}
      `,
      size,
      512,
      512,
      bottomPaddingRatio,
      shiftUpRatio
    );
}
const eraserSvg = (size:number= DEFAULT_TOOLKIT_SIZE, bottomPaddingRatio: number = CURSOR_BOTTOM_PADDING_RATIO, shiftUpRatio: number = CURSOR_SHIFT_UP_RATIO): string => {
  const whitePart = "M193,215.7c-0.8-0.8-0.8-2,0-2.8s2-0.8,2.8,0l150,150l140-140c9.2-9.2,14.4-21.6,14.4-35.2 s-5.2-25.6-14.4-35.2l-106-105.6c-9.2-9.2-21.6-14.4-35.2-14.4s-25.6,5.2-35.2,14.4L20.2,335.7C1,355.3,1,386.9,20.2,406.1 l67.6,67.6h147.6L343,366.1L193,215.7z";
  const blackPart = "M193,215.7c-0.8-0.8-0.8-2,0-2.8s2-0.8,2.8,0l150,150l140-140c9.2-9.2,14.4-21.6,14.4-35.2 s-5.2-25.6-14.4-35.2l-106-105.6c-9.2-9.2-21.6-14.4-35.2-14.4s-25.6,5.2-35.2,14.4L166.6,189.7l176,176L193,215.7z";
  const cutFace = "M168.2,191.3L11.4,348.5c-6.8,13.2-7.2,29.2-0.8,43.2l179.2-179.2L168.2,191.3z";

  return buildPaddedCursorSvg(
    `
      ${outlinedFilledPath(whitePart, SengiColors.sng_white, 24, 12)}
      ${outlinedFilledPath(blackPart, "#000000", 24, 12)}
      ${outlinedFilledPath(cutFace, SengiColors.sng_white, 12, 12)}

      <path fill="#2b2b2b" d="M189.4,212.5l176-175.6c-6.4-2.8-13.6-4.4-20.8-4.4c-8,0-15.6,2-22.4,5.6l-154,153.2L189.4,212.5z"/>
      <path fill="#000000" d="M236.2,479.7H86.6c-1.2,0-2-0.4-2.8-1.2l-67.6-68c-21.6-21.6-21.6-57.2,0-78.8l2.8,2.8l-2.8-2.8L305.4,42.5 c21.6-21.6,56.8-21.6,78.4,0l106,106c21.6,21.6,21.6,57.2,0,78.8L239,478.5C238.2,479.3,237.4,479.7,236.2,479.7z M88.2,471.7h146.4 l249.6-250c8.8-8.8,14-20.8,14-33.6c0-12.8-4.8-24.8-14-33.6l-106-106c-18.4-18.4-48.8-18.4-67.2,0L21.8,337.7 c-18.4,18.4-18.4,49.2,0,67.6L88.2,471.7z"/>
      <path fill="#000000" d="M365,479.7H89c-2.4,0-4-1.6-4-4s1.6-4,4-4h276c2.4,0,4,1.6,4,4S367.4,479.7,365,479.7z"/>
      <path fill="#000000" d="M345,368.9c-1.2,0-2-0.4-2.8-1.2L191.4,217.3c-1.6-1.6-1.6-4,0-5.6s4-1.6,5.6,0l150.8,150.4c1.6,1.6,1.6,4,0,5.6 C347,368.5,346.2,368.9,345,368.9z"/>
      <path fill="#000000" d="M445,463.7H253c-2.4,0-4-1.6-4-4s1.6-4,4-4h192c2.4,0,4,1.6,4,4S447.4,463.7,445,463.7z"/>
    `,
    size,
    512,
    512,
    bottomPaddingRatio,
    shiftUpRatio
  );
}

const fillDripSvg = (
  color: string,
  size: number = DEFAULT_TOOLKIT_SIZE,
  bottomPaddingRatio: number = CURSOR_BOTTOM_PADDING_RATIO, 
  shiftUpRatio: number = CURSOR_SHIFT_UP_RATIO
): string => {
  const bucketBody = `
    M28.172 19.414
    L20.415 27.171
    C19.634 27.952 18.368 27.952 17.587 27.171
    L6.414 16
    L15.586 6.828
    C16.367 6.047 17.633 6.047 18.414 6.828
    L28.172 16.586
    C28.953 17.367 28.953 18.633 28.172 19.414
    Z
  `;

  const bucketTopCut = `
    M14 8.414
    L14 13.05
    C15.141 13.284 16 14.291 16 15.5
    C16 17.108 14.481 18.362 12.8 17.905
    C12.005 17.689 11.349 17.055 11.114 16.266
    C10.659 14.737 11.624 13.329 13 13.05
    L13 9.414
    Z
  `;

  const handle = `
    M14 7
    L14 5
    C14 3.15 15.683 1.686 17.598 2.058
    C19.022 2.335 20 3.668 20 5.118
    L20 7
  `;

  const spoutFlow = `
    M11 10
    L5 16
    L5 28
    C5 28.551 4.552 29 4 29
    C3.448 29 3 28.551 3 28
    L3 15.5
    C3 12.467 5.468 10 8.5 10
    Z
  `;

  const drip = `
    M12 15.5
    C12 16.327 12.673 17 13.5 17
    C14.327 17 15 16.327 15 15.5
    C15 14.849 14.581 14.299 14 14.092
    L14 15.5
    C14 15.776 13.776 16 13.5 16
    C13.224 16 13 15.776 13 15.5
    L13 14.092
    C12.419 14.299 12 14.849 12 15.5
    Z
  `;

  return buildPaddedCursorSvg(
    `
      ${outlinedFilledPath(bucketBody, SengiColors.sng_white, 1.7, 0.5)}
      ${outlinedFilledPath(bucketTopCut, SengiColors.sng_white, 0.8, 0)}
      ${strokedPath(handle, 1, 2)}
      ${outlinedFilledPath(spoutFlow, color, 1.7, 0.5)}
      ${outlinedFilledPath(drip, SengiColors.sng_white, 1.7, 0.5)}
    `,
    size,
    32,
    32,
    bottomPaddingRatio,
    shiftUpRatio
  );
};

const getSvgByType = (type: string, color: string, size:number= DEFAULT_TOOLKIT_SIZE, bottomPaddingRatio: number = CURSOR_BOTTOM_PADDING_RATIO, shiftUpRatio: number = CURSOR_SHIFT_UP_RATIO): string | null => {
  switch (type) {
    case "brush":
      return brushSvg(color,size, bottomPaddingRatio,shiftUpRatio);
    case "eraser":
      return eraserSvg(size,bottomPaddingRatio,shiftUpRatio);
    case "fill":
      return fillDripSvg(color,size,bottomPaddingRatio,shiftUpRatio);
    default:
      return null;
  }
};

export const getDrawingCursorValue = (
  type: string,
  color: string = SengiColors.sng_white,
  hotspot: string = DEFAULT_HOTSPOT,
  size:number= DEFAULT_TOOLKIT_SIZE, 
  bottomPaddingRatio: number = CURSOR_BOTTOM_PADDING_RATIO, 
  shiftUpRatio: number = CURSOR_SHIFT_UP_RATIO
): string | null => {
  const svg = getSvgByType(type, color,size,bottomPaddingRatio,shiftUpRatio);
  if (!svg) {
    return null;
  }
  return buildCursorString(svg, hotspot);
};

export const getDrawingCursorUrl = (
  type: string,
  color: string = SengiColors.sng_white,
  size:number= DEFAULT_TOOLKIT_SIZE, 
  bottomPaddingRatio: number = CURSOR_BOTTOM_PADDING_RATIO, 
  shiftUpRatio: number = CURSOR_SHIFT_UP_RATIO
): string | null => {
  const svg = getSvgByType(type, color,size,bottomPaddingRatio,shiftUpRatio);
  if (!svg) {
    return null;
  }
  return `data:image/svg+xml;utf8,${escapeSvgForCursor(svg)}`;
};

export const getDrawingCursorSvg = (
  type: string,
  color: string = SengiColors.sng_white,
  size:number= DEFAULT_TOOLKIT_SIZE, 
  bottomPaddingRatio: number = CURSOR_BOTTOM_PADDING_RATIO, 
  shiftUpRatio: number = CURSOR_SHIFT_UP_RATIO
): string | null => {
  return getSvgByType(type, color,size,bottomPaddingRatio,shiftUpRatio);
};
